import type { components } from "@/api-schema";
import { VehicleValuationStatus } from "@/api-schema-enums";
import { EMPTY_VALUE_PLACEHOLDER } from "@/components/ui/empty-value";
import i18next from "i18next";

export type VehicleValuation = components["schemas"]["VehicleValuation"];

export interface NumberOfHoldersMismatch {
  current: VehicleValuation;
  reference: VehicleValuation;
}

export interface VehicleValuationHistoryEntry {
  valuation: VehicleValuation;
  previous?: VehicleValuation;
  mismatch?: NumberOfHoldersMismatch;
}

export interface VehicleValuationHistorySummary {
  historyEntries: VehicleValuationHistoryEntry[];
  mismatchEntries: VehicleValuationHistoryEntry[];
}

export function formatIntegerText(value?: number | null): string {
  if (typeof value !== "number") {
    return EMPTY_VALUE_PLACEHOLDER;
  }

  return new Intl.NumberFormat(i18next.language).format(value);
}

export function sortVehicleValuationsByMileageAscending(valuations: VehicleValuation[]): VehicleValuation[] {
  return [...valuations].sort((left, right) => {
    const leftMileage = left.mileageUsed ?? Number.POSITIVE_INFINITY;
    const rightMileage = right.mileageUsed ?? Number.POSITIVE_INFINITY;

    if (leftMileage !== rightMileage) {
      return leftMileage - rightMileage;
    }

    const leftCreatedAt = Date.parse(left.createdAt);
    const rightCreatedAt = Date.parse(right.createdAt);

    if (Number.isFinite(leftCreatedAt) && Number.isFinite(rightCreatedAt) && leftCreatedAt !== rightCreatedAt) {
      return leftCreatedAt - rightCreatedAt;
    }

    return left.uuid.localeCompare(right.uuid);
  });
}

function getVehicleValuationCreatedAtTimestamp(valuation: VehicleValuation): number {
  const createdAt = Date.parse(valuation.createdAt);

  return Number.isFinite(createdAt) ? createdAt : Number.NEGATIVE_INFINITY;
}

export function sortVehicleValuationsByCreatedAtDescending(valuations: VehicleValuation[]): VehicleValuation[] {
  return [...valuations].sort((left, right) => {
    const leftCreatedAt = getVehicleValuationCreatedAtTimestamp(left);
    const rightCreatedAt = getVehicleValuationCreatedAtTimestamp(right);

    if (leftCreatedAt !== rightCreatedAt) {
      return rightCreatedAt - leftCreatedAt;
    }

    return right.uuid.localeCompare(left.uuid);
  });
}

export function hasCalculatedVehicleValuationResult(valuation: VehicleValuation): boolean {
  return (
    valuation.status !== VehicleValuationStatus.Failed &&
    [valuation.tradeGross, valuation.salesGross, valuation.marginGross, valuation.marginPercentage].some(
      (value) => value !== null && value !== undefined,
    )
  );
}

export function buildNumberOfHoldersMismatchMap(
  valuations: VehicleValuation[],
): ReadonlyMap<string, NumberOfHoldersMismatch> {
  const mismatches = new Map<string, NumberOfHoldersMismatch>();
  let maxNumberOfHoldersReference: VehicleValuation | undefined;

  for (const valuation of valuations) {
    if (
      typeof valuation.numberOfHoldersUsed === "number" &&
      maxNumberOfHoldersReference &&
      typeof maxNumberOfHoldersReference.numberOfHoldersUsed === "number" &&
      valuation.numberOfHoldersUsed < maxNumberOfHoldersReference.numberOfHoldersUsed
    ) {
      mismatches.set(valuation.uuid, {
        current: valuation,
        reference: maxNumberOfHoldersReference,
      });
    }

    if (
      typeof valuation.numberOfHoldersUsed === "number" &&
      (!maxNumberOfHoldersReference ||
        typeof maxNumberOfHoldersReference.numberOfHoldersUsed !== "number" ||
        valuation.numberOfHoldersUsed > maxNumberOfHoldersReference.numberOfHoldersUsed)
    ) {
      maxNumberOfHoldersReference = valuation;
    }
  }

  return mismatches;
}

export function buildValuationHistoryEntries(
  valuations: VehicleValuation[],
  mismatchByValuationUuid: ReadonlyMap<string, NumberOfHoldersMismatch>,
): VehicleValuationHistoryEntry[] {
  return valuations.map((valuation, index) => ({
    valuation,
    previous: valuations[index + 1],
    mismatch: mismatchByValuationUuid.get(valuation.uuid),
  }));
}
